/**
 * usePageTransition Hook
 * 
 * Simplified hook for determining appropriate transition type and managing animation state:
 * - Horizontal transitions for most page changes
 * - Vertical transitions for modals and dialogs
 * - Tracks animation state to prevent animation conflicts
 * - Consistently applied across the application
 * 
 * @version 2.2
 */

import { useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

/**
 * Modal-like routes that should use vertical transitions
 */
const VERTICAL_ROUTES = [
  '/profile',
  '/settings',
  '/details'
];

/**
 * Simple hook to determine appropriate transition type and manage animation state
 * 
 * @returns {Object} Transition information including type and animation status
 */
const usePageTransition = () => {
  const location = useLocation();
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Check if current route should use vertical transition
  const isVerticalTransition = VERTICAL_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );
  
  // Reset animation state when location changes
  useEffect(() => {
    // Start animation
    setIsAnimating(true);
    
    // Clear animation state after animation completes
    const timeout = setTimeout(() => {
      setIsAnimating(false);
    }, 350); // Slightly longer than animation to ensure completion
    
    return () => clearTimeout(timeout);
  }, [location.key]);
  
  return {
    type: isVerticalTransition ? 'vertical' : 'horizontal',
    isAnimating
  };
};

export default usePageTransition; 